import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Icon before the label: the icon modifies the verb ("add", "export"). */
  icon?: ReactNode;
  /** Icon after the label. Use only for direction, e.g. a disclosure chevron. */
  trailingIcon?: ReactNode;
  /** Replaces the label with a progress state and blocks further clicks. */
  loading?: boolean;
  fullWidth?: boolean;
}

export type ButtonVariant = Variant;
export type ButtonSize = Size;

/**
 * Blue is the action you are meant to take, so it never competes with the
 * data. Secondary is a white button with a grey edge, ghost is the way out
 * of a decision, and danger is the one that cannot be undone. There was an
 * accent variant too, identical to primary but for its hover step, which
 * nothing used and which only ever offered a way to disagree with this.
 *
 * Sized for a dense tool: 32px in a toolbar or a table row, 36px
 * beside a field, and a medium weight rather than a bold one, because the
 * colour is already saying which button matters.
 */
const VARIANT: Record<Variant, string> = {
  primary:
    "bg-primary text-primary-fg hover:bg-primary-hover active:bg-brand-700 " +
    "disabled:bg-ink-200 disabled:text-fg-placeholder disabled:shadow-none",
  secondary:
    "bg-surface text-fg ring-1 ring-inset ring-line-strong hover:bg-ink-50 " +
    "active:bg-ink-100 disabled:text-fg-placeholder disabled:bg-surface disabled:shadow-none",
  ghost:
    "bg-transparent text-fg-muted hover:bg-ink-100 hover:text-fg active:bg-ink-200 " +
    "disabled:text-fg-placeholder disabled:bg-transparent",
  danger:
    "bg-danger text-white hover:bg-danger-hover active:bg-alert-700 " +
    "disabled:bg-ink-200 disabled:text-fg-placeholder disabled:shadow-none",
};

const SIZE: Record<Size, string> = {
  sm: "h-8 px-2.5 text-xs gap-1.5 rounded-xs",
  md: "h-9 px-3.5 text-base gap-2 rounded-sm",
  lg: "h-11 px-5 text-base gap-2 rounded-sm",
};

/** The shared look, so a link can be styled as a button without duplicating it. */
export function buttonClasses(
  variant: Variant = "secondary",
  size: Size = "md",
  fullWidth = false,
): string {
  return cn(
    "inline-flex items-center justify-center font-medium whitespace-nowrap",
    "transition-[background-color,box-shadow,color] duration-150 disabled:cursor-not-allowed",
    VARIANT[variant],
    SIZE[size],
    fullWidth && "w-full",
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    icon,
    trailingIcon,
    loading = false,
    fullWidth = false,
    className,
    children,
    disabled,
    type = "button",
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonClasses(variant, size, fullWidth), className)}
      {...props}
    >
      {loading ? <Spinner /> : icon ? <span aria-hidden="true">{icon}</span> : null}
      <span className={cn(loading && "opacity-70")}>{children}</span>
      {trailingIcon ? <span aria-hidden="true">{trailingIcon}</span> : null}
    </button>
  );
});

function Spinner() {
  return (
    <svg
      className="size-3.5 animate-spin"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
      <path
        d="M14.5 8A6.5 6.5 0 0 0 8 1.5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
